import { ConversationResponse, MessageResponse } from '@copilot/data/responses/interface';
import { ContactManager, InboxManager, LinkedInManager } from '@copilot/data';
import { arrayToMap, getExpectedOne, isPlainJsonObject, throwError } from '@copilot/common/utils';
import { isNil } from 'lodash';
import { ISmartReply, ISmartReplyError } from '@copilot/data/graphql/_generated';
import {
    WriteSmartReplyErrors,
    WriteSmartReplyMessageInput,
    WriteSmartReplyQuery,
} from '@copilot/common/hooks/smartReply/smartReplyTypes';
import {
    OperationStatus,
    OperationStatuses,
} from '@copilot/common/components/statusIndicator/statusIndicator';
import { ReactNode } from 'react';
import { Typography } from 'antd';
import { Config } from '@copilot/common/config';

const { Text, Link } = Typography;

/**
 * The type of a connection
 */
type ConnectionType = Readonly<{
    /* Connection ID */
    id: string;
    /* Organization member id */
    orgMemberId: string;
    /* Organization member's profile id */
    orgMemberProfileId: string;
    /* The prospect's profile id */
    profileId: string;
}>;

/**
 * Gets the conversation context of and org member and a contact
 * @param connection
 */
async function getConversation(connection: ConnectionType): Promise<ConversationResponse> {
    const conversationResponses = await InboxManager.getMessagesForLinkedInProfile(
        connection.orgMemberProfileId,
        connection.profileId
    );

    return getExpectedOne(conversationResponses);
}

/**
 * Gets the connection context of an org member and a contact
 * @param orgId
 * @param memberId
 * @param contactId
 */
async function getConnection(orgId: string, memberId: string, contactId: string) {
    const contactConnections: Array<{ connections: Array<ConnectionType> }> =
        await ContactManager.getMultiConnection(orgId, contactId);

    const connectionByOrgMemberId = arrayToMap(
        getExpectedOne(contactConnections).connections,
        (connection) => connection.orgMemberId
    );

    const connection =
        connectionByOrgMemberId.get(memberId) ?? throwError('Unable to get conversation context');
    return connection;
}

/**
 * Gets a conversation's context for writing a Smart Reply
 * @param orgId
 * @param memberId
 * @param contactId
 */
export async function getSmartReplyConversationContext(
    orgId: string,
    memberId: string,
    contactId: string
): Promise<{
    sender: { name: string; id?: string };
    recipient: { name: string; urn?: string };
    messageThread: WriteSmartReplyMessageInput[];
    connectionId: string;
}> {
    const connection = await getConnection(orgId, memberId, contactId);
    const conversation = await getConversation(connection);
    const recipientProfile = await LinkedInManager.getContactLinkedInProfile(orgId, contactId);

    return {
        sender: {
            name: conversation.sourceName,
            id: memberId,
        },
        recipient: {
            name: conversation.targetName,
            urn: recipientProfile?.entityUrn,
        },
        messageThread: toMessageThread(conversation),
        connectionId: connection.id,
    };
}

/**
 * Converts a ConversationResponse object to an array of messages ordered by the time they were sent.
 * Omits messages that have not yet been delivered.
 * @param conversation
 */
function toMessageThread({
    receivedMessages,
    sentMessages,
}: ConversationResponse): WriteSmartReplyMessageInput[] {
    return [
        ...toMessageThreadInternal(receivedMessages, false),
        ...toMessageThreadInternal(sentMessages, true),
    ]
        .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
        .map(({ message, outbound }) => ({
            message,
            outbound,
        }));
}

/**
 * Converts an array of messages to an array of WriteSmartReplyMessageInput objects, adding their timestamp.
 * Internal function for toMessageThread.
 * @param messages
 * @param isOutbound
 */
function toMessageThreadInternal(
    messages: ReadonlyArray<MessageResponse>,
    isOutbound: boolean
): ReadonlyArray<WriteSmartReplyMessageInput & Readonly<{ timestamp: Date }>> {
    return messages
        .filter((message) => !isNil(message.timestamp))
        .map((message) => ({
            message: message.data,
            timestamp: new Date(message.timestamp as string),
            outbound: isOutbound,
        }));
}

/**
 * Typeguard for ISmartReplyError
 * @param x
 */
export function isSmartReplyError(x: unknown): x is ISmartReplyError {
    return isPlainJsonObject(x) && x.__typename === 'SmartReplyError';
}

/**
 * Typeguard for ISmartReply
 * @param x
 */
export function isSmartReply(x: unknown): x is ISmartReply {
    return isPlainJsonObject(x) && x.__typename === 'SmartReply';
}

/**
 * Gets the operation status and message for a WriteSmartReplyQuery
 * @param data
 */
export function getOperationStatus(data: WriteSmartReplyQuery): {
    operationStatus: OperationStatus;
    operationMessage: ReactNode;
} {
    if (data.error) {
        if (data.error.message === WriteSmartReplyErrors.moderation) {
            return {
                operationStatus: OperationStatuses.error,
                operationMessage: (
                    <Text>
                        The prompt or conversation history violates{' '}
                        <Link href={Config.termsAndServicesURL} target="_blank">
                            terms of usage
                        </Link>
                        .
                    </Text>
                ),
            };
        }

        return {
            operationStatus: OperationStatuses.error,
            operationMessage: <Text>We're not currently able to connect to this service.</Text>,
        };
    } else if (data.loading) {
        // If data is loading, update status to in progress
        return {
            operationStatus: OperationStatuses.inProgress,
            operationMessage: <Text>AI is writing</Text>,
        };
    } else {
        // If it did not error out nor loading, set operation status to complete
        return {
            operationStatus: OperationStatuses.complete,
            operationMessage: <Text>Done!</Text>,
        };
    }
}
